/**
 * @fileoverview Address Suggestion Dialog Component
 * 
 * Modal dialog that displays address correction suggestions from Smarty
 * when the user's input doesn't exactly match a valid address.
 */

import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, AlertCircle, MapPin } from 'lucide-react';
import { StandardizedAddress, ValidationResult } from '@/hooks/useAddressValidation';
import { useState } from 'react';

interface AddressSuggestionDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when dialog open state changes */
  onOpenChange: (open: boolean) => void;
  /** The original address input by the user */
  originalAddress: {
    street: string;
    city: string;
    state: string;
    zipcode: string;
  };
  /** Validation result containing suggested addresses */
  validationResult: ValidationResult;
  /** Callback when user selects an address */
  onSelectAddress: (address: StandardizedAddress) => void;
  /** Callback when user chooses to keep original (for valid addresses) */
  onKeepOriginal: () => void;
  /** Callback when user wants to edit the address (for invalid addresses) */
  onEditAddress?: () => void;
}

export function AddressSuggestionDialog({
  open,
  onOpenChange,
  originalAddress,
  validationResult,
  onSelectAddress,
  onKeepOriginal,
  onEditAddress,
}: AddressSuggestionDialogProps) {
  const [selectedIndex, setSelectedIndex] = useState<number>(0);

  const formatAddress = (addr: StandardizedAddress | { street: string; city: string; state: string; zipcode: string }) => {
    const parts: string[] = [];
    parts.push(addr.street);
    
    // Check if it's a StandardizedAddress with street2
    if ('street2' in addr && addr.street2) {
      parts.push(addr.street2);
    }
    
    const cityState = [
      addr.city,
      addr.state,
      addr.zipcode,
    ].filter(Boolean).join(', ');
    
    if (cityState) parts.push(cityState);
    
    // Add plus4 if available
    if ('plus4' in addr && addr.plus4 && addr.zipcode) {
      const lastPart = parts[parts.length - 1];
      parts[parts.length - 1] = lastPart.replace(addr.zipcode, `${addr.zipcode}-${addr.plus4}`);
    }
    
    return parts;
  };

  // ONLY include suggestions if the address is actually valid/deliverable
  const hasValidSuggestion = validationResult.standardizedAddress && validationResult.isValid;
  
  // Combine primary suggestion with additional suggestions - only when valid
  const allSuggestions: StandardizedAddress[] = [];
  if (validationResult.isValid) {
    if (validationResult.standardizedAddress) {
      allSuggestions.push(validationResult.standardizedAddress);
    }
    if (validationResult.suggestions) {
      allSuggestions.push(...validationResult.suggestions);
    }
  }

  const handleUseSelected = () => {
    if (allSuggestions[selectedIndex]) {
      onSelectAddress(allSuggestions[selectedIndex]);
    }
    onOpenChange(false);
  };

  const handleKeepOriginal = () => {
    onKeepOriginal();
    onOpenChange(false);
  };

  const handleEditAddress = () => {
    if (onEditAddress) {
      onEditAddress();
    }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Address Verification
          </DialogTitle>
          <DialogDescription>
            {hasValidSuggestion 
              ? 'We found a verified address. Would you like to use the standardized format?'
              : 'The address you entered could not be verified. Please check and try again.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Original Address */}
          <div className="space-y-2">
            <p className="text-sm font-medium text-muted-foreground">You entered:</p>
            <Card className="p-3 bg-muted/50">
              <div className="text-sm">
                {formatAddress(originalAddress).map((line, i) => (
                  <p key={i}>{line}</p>
                ))}
              </div>
            </Card>
          </div>

          {/* Suggestions - only shown when address is valid */}
          {allSuggestions.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-muted-foreground">
                {allSuggestions.length === 1 ? 'Verified address:' : 'Suggested addresses:'}
              </p>
              <div className="space-y-2">
                {allSuggestions.map((suggestion, index) => (
                  <Card
                    key={index}
                    className={`p-3 cursor-pointer transition-colors ${
                      selectedIndex === index 
                        ? 'border-primary bg-primary/5' 
                        : 'hover:bg-secondary/50'
                    }`}
                    onClick={() => setSelectedIndex(index)}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="text-sm">
                        {formatAddress(suggestion).map((line, i) => (
                          <p key={i}>{line}</p>
                        ))}
                        {suggestion.county && (
                          <p className="text-xs text-muted-foreground mt-1">
                            {suggestion.county} County
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {index === 0 && validationResult.deliverability === 'deliverable' && (
                          <Badge variant="default" className="bg-green-100 text-green-800 border-green-200">
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Verified
                          </Badge>
                        )}
                        {index === 0 && validationResult.deliverability === 'partially_deliverable' && (
                          <Badge variant="default" className="bg-yellow-100 text-yellow-800 border-yellow-200">
                            <AlertCircle className="h-3 w-3 mr-1" />
                            Partial
                          </Badge>
                        )}
                        {selectedIndex === index && (
                          <div className="w-4 h-4 rounded-full bg-primary flex items-center justify-center">
                            <CheckCircle2 className="h-3 w-3 text-primary-foreground" />
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Address not found warning - shown when address is NOT valid */}
          {!validationResult.isValid && (
            <Card className="p-4 border-destructive/50 bg-destructive/5">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-destructive">Address Not Found</p>
                  <p className="text-muted-foreground mt-1">
                    {validationResult.error || 'This address could not be verified. Please double-check the street address, city, state, and ZIP code.'}
                  </p>
                </div>
              </div>
            </Card>
          )}
        </div>

        <DialogFooter className="flex gap-2 sm:gap-2">
          <Button 
            variant="outline" 
            onClick={validationResult.isValid ? handleKeepOriginal : handleEditAddress}
          >
            {validationResult.isValid ? 'Keep Original' : 'Edit Address'}
          </Button>
          {!validationResult.isValid && (
            <Button 
              variant="secondary"
              onClick={handleKeepOriginal}
            >
              Use Anyway
            </Button>
          )}
          {allSuggestions.length > 0 && (
            <Button onClick={handleUseSelected}>
              Use Selected Address
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
